import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Trash2,
  User,
  Calendar,
  Tag,
  DollarSign,
  FileText,
  ChevronDown,
  AlertCircle,
  Edit
} from 'lucide-react'
import { getColors } from '../../../theme/colors'
import {
  deleteEntry,
  closeCashBook
} from '../../../store/slices/cashDayBookSlice'
import { dateUtils } from '../../../api'

const EntriesTable = ({
  entries,
  loading,
  pagination,
  isAdmin,
  activeTab,
  onLoadMore,
  onEditEntry
}) => {
  const dispatch = useDispatch()
  const colors = getColors()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null)
  const [showCloseConfirm, setShowCloseConfirm] = useState(null)

  const handleDelete = async entryId => {
    try {
      await dispatch(deleteEntry(entryId)).unwrap()
      setShowDeleteConfirm(null)
    } catch (error) {
      // Error handled by slice
    }
  }

  const handleCloseCashBook = async userId => {
    try {
      await dispatch(closeCashBook({ userId })).unwrap()
      setShowCloseConfirm(null)
    } catch (error) {
      // Error handled by slice
    }
  }

  if (loading && !entries.length) {
    return (
      <div
        className={`${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-xl p-6`}
      >
        <div className='animate-pulse space-y-4'>
          <div className='h-6 bg-gray-600 rounded w-1/4'></div>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className='flex space-x-4'>
              <div className='h-4 bg-gray-600 rounded w-1/6'></div>
              <div className='h-4 bg-gray-600 rounded w-1/4'></div>
              <div className='h-4 bg-gray-600 rounded w-1/6'></div>
              <div className='h-4 bg-gray-600 rounded w-1/4'></div>
              <div className='h-4 bg-gray-600 rounded w-1/6'></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!entries.length) {
    return (
      <div
        className={`${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-xl p-8 text-center`}
      >
        <DollarSign className={`${colors.textMuted} mx-auto mb-4`} size={48} />
        <h3 className={`${colors.textPrimary} text-xl font-semibold mb-2`}>
          No entries found
        </h3>
        <p className={`${colors.textSecondary}`}>
          Start by adding your first income or expense entry.
        </p>
      </div>
    )
  }

  return (
    <div
      className={`${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-xl overflow-hidden`}
    >
      {/* Header */}
      <div
        className={`${colors.tableHeaderBg} px-6 py-4 border-b ${colors.tableBorderColor}`}
      >
        <h3
          className={`${colors.textPrimary} text-lg font-semibold flex items-center gap-2`}
        >
          <FileText size={20} />
          Cash Day Book Entries
          <span className={`${colors.textMuted} text-sm font-normal ml-2`}>
            ({pagination.count} total entries)
          </span>
        </h3>
      </div>

      {/* Table */}
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead
            className={`${colors.tableHeaderBg} border-b ${colors.tableBorderColor}`}
          >
            <tr>
              <th
                className={`${colors.textPrimary} text-left py-3 px-4 font-medium`}
              >
                Date
              </th>
              <th
                className={`${colors.textPrimary} text-left py-3 px-4 font-medium`}
              >
                Category
              </th>
              <th
                className={`${colors.textPrimary} text-left py-3 px-4 font-medium`}
              >
                Type
              </th>
              <th
                className={`${colors.textPrimary} text-right py-3 px-4 font-medium`}
              >
                Amount
              </th>
              {activeTab === 'all' && (
                <th
                  className={`${colors.textPrimary} text-left py-3 px-4 font-medium`}
                >
                  User
                </th>
              )}
              <th
                className={`${colors.textPrimary} text-left py-3 px-4 font-medium`}
              >
                Description
              </th>
              {isAdmin && (
                <th
                  className={`${colors.textPrimary} text-center py-3 px-4 font-medium`}
                >
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, index) => (
              <tr
                key={entry._id}
                className={`border-b ${colors.tableBorderColor} ${colors.tableRowHover} transition-all duration-200 hover:bg-opacity-50 group`}
              >
                <td className={`${colors.textSecondary} py-3 px-4`}>
                  <div className='flex items-center gap-2'>
                    <Calendar size={16} />
                    {dateUtils.formatDate(entry.date)}
                  </div>
                </td>
                <td className={`${colors.textPrimary} py-3 px-4 font-medium`}>
                  <div className='flex items-center gap-2'>
                    <Tag size={16} className={`${colors.textHighlight} opacity-70 group-hover:opacity-100 transition-opacity`} />
                    {entry.category}
                  </div>
                </td>
                <td className='py-3 px-4'>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${entry.type === 'Income'
                        ? 'bg-emerald-500 bg-opacity-20 text-emerald-400'
                        : 'bg-red-500 bg-opacity-20 text-red-400'
                      }`}
                  >
                    {entry.type === 'Income' ? '↗ Income' : '↘ Expense'}
                  </span>
                </td>
                <td
                  className={`py-3 px-4 text-right font-semibold ${entry.type === 'Income' ? colors.positive : colors.negative
                    }`}
                >
                  {dateUtils.formatCurrency(entry.amount)}
                </td>
                {activeTab === 'all' && (
                  <td className={`${colors.textSecondary} py-3 px-4`}>
                    <div className='flex items-center gap-2'>
                      <User size={16} />
                      <div>
                        <div className={colors.textPrimary}>
                          {entry.user?.username}
                        </div>
                        {entry.user?.employeeData && (
                          <div className={`${colors.textMuted} text-xs`}>
                            {entry.user.employeeData.designation}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                )}
                <td className={`${colors.textSecondary} py-3 px-4 max-w-xs`}>
                  <div className='truncate' title={entry.description}>
                    {entry.description || '-'}
                  </div>
                </td>
                {isAdmin && (
                  <td className='py-3 px-4 text-center'>
                    <div className='flex items-center justify-center gap-2'>
                      <button
                        onClick={() => onEditEntry(entry)}
                        className={`${colors.textHighlight} hover:text-teal-300 p-1 rounded hover:bg-teal-500 hover:bg-opacity-20 transition-colors`}
                        title='Edit entry'
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(entry._id)}
                        className='text-red-400 hover:text-red-300 p-1 rounded hover:bg-red-500 hover:bg-opacity-20 transition-colors'
                        title='Delete entry'
                      >
                        <Trash2 size={16} />
                      </button>
                      {activeTab === 'all' && (
                        <button
                          onClick={() => setShowCloseConfirm(entry.user._id)}
                          className={`${colors.textHighlight} hover:text-teal-300 p-1 rounded hover:bg-teal-500 hover:bg-opacity-20 transition-colors`}
                          title="Close user's cash book"
                        >
                          <AlertCircle size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Load More Button */}
      {pagination.current < pagination.total && (
        <div className='p-6 border-t border-gray-700 text-center'>
          <button
            onClick={onLoadMore}
            disabled={loading}
            className={`bg-gradient-to-r ${colors.btnSecondaryFrom} ${colors.btnSecondaryTo} ${colors.textPrimary} px-6 py-2 rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto`}
          >
            {loading ? (
              'Loading...'
            ) : (
              <>
                <ChevronDown size={16} />
                Load More ({pagination.total - pagination.current} more pages)
              </>
            )}
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className={`${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-xl p-6 max-w-md w-full`}
          >
            <div className='text-center'>
              <AlertCircle className='text-red-400 mx-auto mb-4' size={48} />
              <h3
                className={`${colors.textPrimary} text-lg font-semibold mb-2`}
              >
                Confirm Deletion
              </h3>
              <p className={`${colors.textSecondary} mb-6`}>
                Are you sure you want to delete this entry? This action cannot
                be undone.
              </p>
              <div className='flex gap-3'>
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className={`flex-1 bg-gradient-to-r ${colors.btnSecondaryFrom} ${colors.btnSecondaryTo} ${colors.textPrimary} py-2 px-4 rounded-lg hover:shadow-lg transition-all duration-300`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className='flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-2 px-4 rounded-lg hover:shadow-lg transition-all duration-300'
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close Cash Book Confirmation Modal */}
      {showCloseConfirm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className={`${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-xl p-6 max-w-md w-full`}
          >
            <div className='text-center'>
              <AlertCircle
                className={`${colors.textHighlight} mx-auto mb-4`}
                size={48}
              />
              <h3
                className={`${colors.textPrimary} text-lg font-semibold mb-2`}
              >
                Close Cash Book
              </h3>
              <p className={`${colors.textSecondary} mb-6`}>
                Are you sure you want to close this user's cash book? This will
                finalize all open entries.
              </p>
              <div className='flex gap-3'>
                <button
                  onClick={() => setShowCloseConfirm(null)}
                  className={`flex-1 bg-gradient-to-r ${colors.btnSecondaryFrom} ${colors.btnSecondaryTo} ${colors.textPrimary} py-2 px-4 rounded-lg hover:shadow-lg transition-all duration-300`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleCloseCashBook(showCloseConfirm)}
                  className={`flex-1 bg-gradient-to-r ${colors.btnPrimaryFrom} ${colors.btnPrimaryTo} ${colors.textPrimary} py-2 px-4 rounded-lg hover:shadow-lg transition-all duration-300`}
                >
                  Close Book
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EntriesTable
